import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Divider, Link, Skeleton, Typography } from '@mui/material';
import { ROUTE_STRING } from 'constants/RouteString';
import { isSafeExternalUrl } from 'redux/slices/pipelines/helpers/isSafeExternalUrl';
import { resolveEffectiveBranchLabel } from 'redux/slices/pipelines/helpers/resolveEffectiveBranchLabel';
import TargetDriftBadge from './TargetDriftBadge';

// AC-7 — best-effort friendly label for a repository URL (no dedicated "repository name"
// field exists on `PipelineBuildSourceDto` — only `RepositoryUrl`). Falls back to the raw
// URL when parsing fails, so this never throws and never hides a value.
const getRepositoryDisplayName = url => {
	if (!url) {
		return url;
	}
	try {
		const path = new URL(url).pathname.replace(/\/$/, '');
		const lastSegment = path.split('/').filter(Boolean).pop();
		return (lastSegment || url).replace(/\.git$/i, '');
	} catch {
		return url;
	}
};

// A single "label / value" coordinate row. `monospace` mirrors the mock's density cue on
// coordinate values only (Adapt — the theme has no `--font-mono` token).
const CoordinateRow = ({ label, children }) => (
	<Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
		<Typography variant="caption" color="text.secondary" sx={{ width: 72, flexShrink: 0 }}>
			{label}
		</Typography>
		<Box minWidth={0} flex={1}>
			{children}
		</Box>
	</Box>
);

CoordinateRow.propTypes = {
	label: PropTypes.string.isRequired,
	children: PropTypes.node
};

// AC-7/AC-8/AC-10/AC-20/AC-21/AC-23 — the config panel: source coordinates (Branch is the
// LEAD field, per OQ-1/the fidelity audit) + the resolved target. Every value here is
// READ-ONLY display text — no editable tag, no branch picker, no Dockerfile edit affordance
// (all three are engine capabilities the product does not have, AC-20/21/23).
const SourceConfigPanel = ({
	imageName,
	buildSource,
	isLoadingBuildSource,
	isBuildSourceError,
	containerName,
	appInstanceName,
	appInstanceId,
	containerAppId,
	isContainerResolving,
	isContainerUnavailable,
	targetDriftStatus,
	deployedTagVersion,
	latestTagVersion
}) => {
	const isResolved = !!buildSource?.Resolved;
	const repositoryIsSafe = isSafeExternalUrl(buildSource?.RepositoryUrl);
	const repositoryDisplayName = getRepositoryDisplayName(buildSource?.RepositoryUrl);

	return (
		<Box display="flex" flexDirection="column" gap={3}>
			<Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
				Source
			</Typography>

			{isLoadingBuildSource ? (
				<Skeleton variant="rounded" height={140} />
			) : isBuildSourceError ? (
				// A FAILED build-source request is not the same fact as "never built" (adversarial-review
				// finding): the old code collapsed both into the copy below, so a transient 500 made the page
				// state as fact that a pipeline with dozens of builds had none. We do not know either way
				// here, so say exactly that — and keep the Image row visible, since ImageName comes from the
				// PipelineDto this panel already has and does not depend on this request at all.
				<Box display="flex" flexDirection="column" gap={1.5}>
					<CoordinateRow label="Image">
						<Typography variant="body2" noWrap title={imageName} sx={{ fontFamily: 'monospace', fontSize: 12 }}>
							{imageName}
						</Typography>
					</CoordinateRow>
					<Typography variant="body2" color="text.secondary">
						Could not load this pipeline&apos;s build coordinates.
					</Typography>
				</Box>
			) : !isResolved ? (
				// AC-8 — genuinely never-built ImageName: an explicit state, no blank/undefined rows, no
				// error. The Image row is repeated here for the same reason as above — which image this
				// pipeline builds is a fact the page holds regardless of whether a build exists yet.
				<Box display="flex" flexDirection="column" gap={1.5}>
					<CoordinateRow label="Image">
						<Typography variant="body2" noWrap title={imageName} sx={{ fontFamily: 'monospace', fontSize: 12 }}>
							{imageName}
						</Typography>
					</CoordinateRow>
					<Typography variant="body2" color="text.disabled">
						Not built yet — this image has no successful build.
					</Typography>
				</Box>
			) : (
				<Box display="flex" flexDirection="column" gap={1.5}>
					<CoordinateRow label="Image">
						<Typography variant="body2" noWrap title={imageName} sx={{ fontFamily: 'monospace', fontSize: 12 }}>
							{imageName}
						</Typography>
					</CoordinateRow>

					{/* AC-7/AC-21 — Branch is the LEAD field: a single unwrapped, display-only value.
					    deploy-pipelines-run-overrides (Wave 4, AC-9) — the value shown is the RESOLVED
					    EffectiveBranchName (the branch the NEXT run will actually build), not the raw
					    image-group BranchName above it — those two diverge the moment a pipeline is
					    pinned (AC-6) or has its own saved default (OQ-A). The parenthetical labels which
					    of the two states produced it, so a reader is never left inferring it. */}
					<CoordinateRow label="Branch">
						<Box display="flex" alignItems="baseline" gap={1} flexWrap="wrap">
							<Typography variant="subtitle2" noWrap title={buildSource.EffectiveBranchName} sx={{ fontFamily: 'monospace' }}>
								{buildSource.EffectiveBranchName}
							</Typography>
							<Typography
								variant="caption"
								color="text.secondary"
								title={
									buildSource.IsPipelineOwnedDefault
										? "This pipeline's own saved/pinned default — no longer follows the image build source."
										: 'The image build source\'s current branch — not yet pinned to this pipeline.'
								}
							>
								({resolveEffectiveBranchLabel(buildSource.IsPipelineOwnedDefault)})
							</Typography>
						</Box>
					</CoordinateRow>

					{/* AC-7/AC-23 — single-line ellipsis, full path via `title`; read-only, no pencil. */}
					<CoordinateRow label="Dockerfile">
						<Typography
							variant="body2"
							noWrap
							title={buildSource.DockerfilePath}
							sx={{ fontFamily: 'monospace', fontSize: 12, textOverflow: 'ellipsis' }}
						>
							{buildSource.DockerfilePath}
						</Typography>
					</CoordinateRow>

					{/* AC-7 — a name/link, never the four-line break-all URL. Only http/https is
					    ever linkified (NFR link-scheme); any other scheme renders as inert text. */}
					<CoordinateRow label="Repository">
						{repositoryIsSafe ? (
							<Link
								href={buildSource.RepositoryUrl}
								target="_blank"
								rel="noopener noreferrer"
								title={buildSource.RepositoryUrl}
								variant="body2"
								noWrap
								sx={{ fontFamily: 'monospace', fontSize: 12, display: 'block' }}
							>
								{repositoryDisplayName}
							</Link>
						) : (
							<Typography variant="body2" noWrap title={buildSource.RepositoryUrl} sx={{ fontFamily: 'monospace', fontSize: 12 }}>
								{repositoryDisplayName}
							</Typography>
						)}
					</CoordinateRow>

					{/* AC-20 — display-only caption; never the currently displayed run's tag (a
					    PipelineRunDto carries no TagVersion).

					    Sourced from `latestTagVersion` (the PipelineDto's latest BUILT tag, the same
					    value the drift badge compares against) rather than `buildSource.TagVersion`.
					    Those two differ: `GetBuildSourceAsync` deliberately resolves the max row
					    UNFILTERED because it answers "what git coordinates will the next run build
					    from", and the trigger path takes those even from a row whose own build failed.
					    Using it for a caption that says "Latest build" would state a failed attempt as a
					    build, and would contradict the drift caption on the same screen
					    (adversarial-review follow-up). Omitted entirely when no build exists — never
					    "tag undefined". */}
					{latestTagVersion !== undefined && latestTagVersion !== null && (
						<Typography variant="caption" color="text.disabled">
							Latest build: tag {latestTagVersion}
						</Typography>
					)}
				</Box>
			)}

			<Divider />

			<Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
				Target
			</Typography>

			{isContainerUnavailable ? (
				// AC-10 — orphaned pipeline (absent from every group, or container missing from
				// the instance): graceful fallback, never a dead link or the raw ContainerAppId int.
				<Typography variant="body2" color="text.secondary">
					Container unavailable
				</Typography>
			) : isContainerResolving ? (
				<Skeleton variant="rounded" height={48} />
			) : (
				<Box display="flex" flexDirection="column" gap={1.5}>
					<CoordinateRow label="Container">
						<Link
							component={RouterLink}
							to={ROUTE_STRING.CONTAINER_APP.VIEW.replace(':appInstanceId', appInstanceId).replace(':id', containerAppId)}
							sx={{ fontFamily: 'monospace', fontSize: 12 }}
							noWrap
						>
							{containerName}
						</Link>
					</CoordinateRow>
					<CoordinateRow label="Instance">
						<Typography variant="body2" noWrap title={appInstanceName} sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}>
							{appInstanceName}
						</Typography>
					</CoordinateRow>
					{/* deploy-pipelines-run-provenance (Wave 2) AC-6/AC-7/AC-8/AC-9 — the drift badge sits
					    in the TARGET region because it is a fact about the container, not about a run.
					    Server-derived and delivered on the GET /api/pipelines/{id} payload this page
					    already fetches — no new request, no new poller (NFR-Performance). */}
					<CoordinateRow label="Sync">
						<TargetDriftBadge status={targetDriftStatus} deployedTagVersion={deployedTagVersion} latestTagVersion={latestTagVersion} />
					</CoordinateRow>
				</Box>
			)}
		</Box>
	);
};

export default React.memo(SourceConfigPanel);

SourceConfigPanel.propTypes = {
	imageName: PropTypes.string,
	buildSource: PropTypes.shape({
		Resolved: PropTypes.bool,
		TagVersion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		BranchName: PropTypes.string,
		DockerfilePath: PropTypes.string,
		RepositoryUrl: PropTypes.string,
		// deploy-pipelines-run-overrides (Wave 4, AC-9).
		EffectiveBranchName: PropTypes.string,
		IsPipelineOwnedDefault: PropTypes.bool
	}),
	isLoadingBuildSource: PropTypes.bool,
	isBuildSourceError: PropTypes.bool,
	containerName: PropTypes.string,
	appInstanceName: PropTypes.string,
	appInstanceId: PropTypes.string,
	containerAppId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	isContainerResolving: PropTypes.bool,
	isContainerUnavailable: PropTypes.bool,
	targetDriftStatus: PropTypes.number,
	deployedTagVersion: PropTypes.number,
	latestTagVersion: PropTypes.number
};
